// ── Чистые утилиты — без React, без состояния ────────────────────────────────
// Форматирование, парсинг, localStorage-хелперы, активность, словарь.

import { markSetting } from './settings';
import { normalizeTheme } from './themes';

import { t, LANG, uiLocale } from './i18n';
// ─── Форматирование времени ───────────────────────────────────────────────────

/** "125.4" → "2:05" */
export const fmt = (t) => {
  if (!t || isNaN(t)) return "0:00";
  return `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, "0")}`;
};

/** "2:05" или "125.4" → секунды (число) */
export const parseTC = (s) => {
  const p = s.trim().split(":");
  if (p.length === 2) return parseInt(p[0]) * 60 + parseFloat(p[1]);
  return parseFloat(p[0]) || 0;
};

// ─── Форматирование дат / байт ────────────────────────────────────────────────

/** timestamp → «12 окт.» (год — только если не текущий: «5 янв. 2024 г.») */
export const dateStr = (ts) => {
  const d = new Date(ts);
  if (isNaN(d)) return "";
  const opts = d.getFullYear() === new Date().getFullYear()
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" };
  return d.toLocaleDateString(uiLocale(), opts);
};

/** байты → "12 МБ" / "1.3 ГБ" */
export const fmtBytes = (b) => {
  if (!b) return t("0 МБ");
  const mb = b / 1024 / 1024;
  return mb > 1024 ? t("{0} ГБ",[(mb / 1024).toFixed(1)]) : t("{0} МБ",[mb.toFixed(0)]);
};

// ─── Тема ─────────────────────────────────────────────────────────────────────

// Темы — ключи THEMES в src/themes.js (light, dark, arcade). Прежняя «eye» (сепия)
// больше не предлагается и открывается как light.
export const getSavedTheme = () => {
  try { return normalizeTheme(localStorage.getItem("ac_theme")); } catch { return "light"; }
};

export const saveTheme = (t) => {
  try { localStorage.setItem("ac_theme", t); } catch {}
  markSetting("ac_theme");
};

// ─── Активный язык (LingQ-style) ──────────────────────────────────────────────
// Все новые проекты и любая записываемая статистика по дефолту попадает в этот язык.
// Проект может переопределить через свой `src_lang`.
const ACTIVE_LANG_KEY = "ac_active_lang";

export const getActiveLang = () => {
  try { return localStorage.getItem(ACTIVE_LANG_KEY) || ""; } catch { return ""; }
};

export const saveActiveLang = (l) => {
  try { localStorage.setItem(ACTIVE_LANG_KEY, (l || "").toLowerCase().slice(0, 8)); } catch {}
  markSetting(ACTIVE_LANG_KEY);
};

/** Резолвит язык проекта или активный; если оба пусты — пустая строка (учитывается как unattributed). */
export const resolveLang = (projectSrcLang) => {
  const explicit = (projectSrcLang || "").toString().toLowerCase().slice(0, 8);
  if (explicit) return explicit;
  return getActiveLang();
};

// ─── Пользовательские языки ──────────────────────────────────────────────────
// Если кто-то хочет учить язык, которого нет во встроенном списке (валлийский,
// баскский, навахо, esperanto...), он добавляет свой через «+ Свой язык».
// Формат записи в localStorage — массив объектов {code, flag, name}.
const CUSTOM_LANGS_KEY = "ac_custom_langs";
const CUSTOM_LANGS_EVENT = "ac_custom_langs_changed";

/** Загружает список пользовательских языков из localStorage. */
export const getCustomLangs = () => {
  try {
    const raw = localStorage.getItem(CUSTOM_LANGS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(x => x && typeof x.code === "string" && x.code.trim())
      .map(x => ({
        code: x.code.toLowerCase().slice(0, 8),
        flag: (x.flag || "🌐").slice(0, 4),
        name: (x.name || x.code).slice(0, 40),
      }));
  } catch { return []; }
};

/** Сохраняет список пользовательских языков и оповещает подписчиков. */
export const saveCustomLangs = (list) => {
  try {
    const cleaned = (Array.isArray(list) ? list : [])
      .filter(x => x && x.code)
      .map(x => ({
        code: String(x.code).toLowerCase().slice(0, 8),
        flag: String(x.flag || "🌐").slice(0, 4),
        name: String(x.name || x.code).slice(0, 40),
      }));
    localStorage.setItem(CUSTOM_LANGS_KEY, JSON.stringify(cleaned));
    try { window.dispatchEvent(new Event(CUSTOM_LANGS_EVENT)); } catch {}
  } catch {}
};

/** Добавляет один язык. Возвращает обновлённый список. */
export const addCustomLang = ({ code, flag, name }) => {
  const norm = (code || "").toLowerCase().trim().slice(0, 8);
  if (!norm) return getCustomLangs();
  const list = getCustomLangs().filter(x => x.code !== norm);
  list.push({ code: norm, flag: (flag || "🌐").slice(0, 4), name: (name || norm).slice(0, 40) });
  saveCustomLangs(list);
  return list;
};

/** Удаляет язык по коду. */
export const removeCustomLang = (code) => {
  const norm = (code || "").toLowerCase().trim();
  const list = getCustomLangs().filter(x => x.code !== norm);
  saveCustomLangs(list);
  return list;
};

/** Имя ключа события, на которое подписываются хуки в App.js. */
export const CUSTOM_LANGS_CHANGE_EVENT = CUSTOM_LANGS_EVENT;

/**
 * Автодетект языка текста по скрипту. Возвращает код только при высокой уверенности
 * (CJK, кириллица, hangul, arabic и т.д.). Для латинского письма возвращает null
 * — там без статистических моделей надёжно отличить нельзя.
 */
export const detectTextLangByScript = (text) => {
  if (!text || typeof text !== "string") return null;
  // Sample first 4KB — этого достаточно для определения скрипта
  const sample = text.slice(0, 4000);
  // Hiragana или katakana → японский (даже если есть kanji)
  if (/[぀-ゟ゠-ヿ]/.test(sample)) return "ja";
  // Hangul → корейский
  if (/[가-힯ᄀ-ᇿ㄰-㆏]/.test(sample)) return "ko";
  // Han (только если нет кана/хангыля выше) → китайский
  if (/[一-鿿㐀-䶿]/.test(sample)) return "zh";
  // Кириллица → русский (по умолчанию; для uk/bg/be пользователь поправит вручную)
  if (/[Ѐ-ӿ]/.test(sample)) return "ru";
  // Арабская вязь
  if (/[؀-ۿݐ-ݿ]/.test(sample)) return "ar";
  // Иврит
  if (/[֐-׿]/.test(sample)) return "he";
  // Тайский
  if (/[฀-๿]/.test(sample)) return "th";
  // Греческий
  if (/[Ͱ-Ͽ]/.test(sample)) return "el";
  // Деванагари → хинди (или маратхи/санскрит — упрощаем до хинди)
  if (/[ऀ-ॿ]/.test(sample)) return "hi";
  return null;
};

// ─── Платформа ────────────────────────────────────────────────────────────────

export const isNative = () =>
  typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();

// ─── Числа по-русски ──────────────────────────────────────────────────────────
// «1 карточка, 2 карточки, 5 карточек, 21 карточка» — правила берём у браузера.
const _ruRules = typeof Intl !== "undefined" && Intl.PluralRules ? new Intl.PluralRules("ru-RU") : null;
// На английском и испанском форм две: «1 card / 5 cards» — берём перевод формы «один» и «много».
export const ruPlural = (n, one, few, many) => {
  if (LANG !== "ru") return Math.abs(Number(n) || 0) === 1 ? t(one) : t(many);
  const r = _ruRules ? _ruRules.select(Math.abs(Number(n) || 0)) : "many";
  return r === "one" ? one : r === "many" ? many : few;
};
export const nCards = (n) => `${n} ${ruPlural(n, "карточка", "карточки", "карточек")}`;
export const nWords = (n) => `${n} ${ruPlural(n, "слово", "слова", "слов")}`;
export const nDays = (n) => `${n} ${ruPlural(n, "день", "дня", "дней")}`;

export const isElectron = () =>
  typeof window !== "undefined" && !!window.electronAPI;

// ─── Парсинг субтитров (SRT / VTT / кастомный формат) ────────────────────────

/** "00:01:23,456" или "1:23.456" → секунды */
export const parseTsSrt = (ts) => {
  const c = ts.trim().replace(",", ".");
  const p = c.split(":");
  if (p.length === 3) return parseInt(p[0]) * 3600 + parseInt(p[1]) * 60 + parseFloat(p[2]);
  if (p.length === 2) return parseInt(p[0]) * 60 + parseFloat(p[1]);
  return parseFloat(c) || 0;
};

/**
 * Парсит текст субтитров (SRT/VTT + кастомный формат с → для перевода).
 * Возвращает [{timeSec, word, translation}].
 * Кастомный формат: строки до → = фраза, строки после → = перевод.
 */
export const parseSubtitles = (text) => {
  const norm = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const blocks = norm.split(/\n\s*\n/).filter(b => b.trim());
  const result = [];

  for (const block of blocks) {
    const lines = block.trim().split("\n").map(l => l.trim()).filter(l => l);
    if (!lines.length || lines[0] === "WEBVTT" || lines[0].startsWith("WEBVTT")) continue;

    let tsIdx = -1, timeSec = null;
    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(/(\d{1,2}:\d{2}(?::\d{2})?[,\.]\d+)\s*-->/);
      if (m) { timeSec = parseTsSrt(m[1]); tsIdx = i; break; }
    }
    if (tsIdx === -1 || timeSec === null) continue;

    const textLines = lines.slice(tsIdx + 1)
      .map(l => l.replace(/<[^>]*>/g, "").replace(/\{[^}]*\}/g, "").trim())
      .filter(l => l);
    if (!textLines.length) continue;

    // Строки до → = фраза; строки после → = перевод
    const arrowIdx = textLines.findIndex(l => /^[→\->]/.test(l));
    let word, translation;
    if (arrowIdx !== -1) {
      word = arrowIdx > 0
        ? textLines.slice(0, arrowIdx).join(" ").trim()
        : textLines[arrowIdx].replace(/^[→\->]\s*/, "");
      translation = textLines.slice(arrowIdx).map(l => l.replace(/^[→\->]\s*/, "")).join(" ").trim();
      if (arrowIdx === 0) { word = translation; translation = ""; }
    } else {
      word = textLines[0];
      translation = textLines.slice(1).join(" ").trim();
    }
    if (!word) continue;
    result.push({ timeSec, word, translation });
  }
  return result;
};

// ─── Активность / Тепловая карта ─────────────────────────────────────────────
// Per-language storage: {lang: {dateKey: count}}. Empty lang ('') = unattributed (legacy).
//
// Date keys are LOCAL "YYYY-MM-DD" (not UTC). Using toISOString().slice(0,10)
// would record activity under the UTC date — for a user in UTC+3 studying at
// 01:00 local, that's the previous UTC day → streak walks back from "today"
// (local) and finds nothing → eternally shows 0. localDateKey() avoids that.

const ACT_KEY = "ac_activity_by_lang";
const ACT_EVT = "ac:activity-changed";
const normLang = (l) => (l || "").toString().toLowerCase().slice(0, 8);
// Запись без языка (проект без языка) идёт в изучаемый язык: иначе она копилась
// в «общей» статистике и не была видна ни в одном языке — её нельзя было очистить.
const recLang = (l) => normLang(l) || normLang(getActiveLang());

/** Local-timezone YYYY-MM-DD. */
export const localDateKey = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Сырое хранилище — {lang: {dateKey: count}}. */
const getActivityRaw = () => {
  try { return JSON.parse(localStorage.getItem(ACT_KEY) || "{}"); } catch { return {}; }
};

/**
 * Возвращает {dateKey: count}. Без lang — суммирует по всем языкам (для совместимости).
 * С lang — данные только этого языка.
 */
export const getActivityData = (lang) => {
  const raw = getActivityRaw();
  if (lang != null) return raw[normLang(lang)] || {};
  const out = {};
  Object.values(raw).forEach(byDate => {
    Object.entries(byDate || {}).forEach(([k, v]) => { out[k] = (out[k] || 0) + (Number(v) || 0); });
  });
  return out;
};

/** Список языков, по которым есть хоть какие-то данные активности. */
export const getActivityLangs = () => Object.keys(getActivityRaw()).filter(l => l);

/** Записывает n повторений на сегодня для указанного языка. */
export const recordActivity = (n = 1, lang = "") => {
  try {
    const raw = getActivityRaw();
    const L = recLang(lang);
    if (!raw[L]) raw[L] = {};
    const today = localDateKey();
    raw[L][today] = (raw[L][today] || 0) + n;
    localStorage.setItem(ACT_KEY, JSON.stringify(raw));
    // Notify listeners (streak badge, daily-goal widget) in same tab.
    // `storage` event covers cross-tab; this covers same-tab updates.
    try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  } catch {}
};

/**
 * Statistics over an activity map {YYYY-MM-DD: count}.
 *
 * `currentStreak` follows the Duolingo rule:
 *   — If the user studied TODAY, the streak counts back from today.
 *   — If the user studied YESTERDAY but not yet today, the streak still shows
 *     yesterday's value (grace period — they can still save it before midnight).
 *   — If they missed both today and yesterday, the streak is broken (0).
 *
 * `longestStreak` is computed in O(n log n) over recorded days only, not by
 * iterating every calendar day since the first activity.
 */
export function calcStats(activity) {
  const keys = Object.keys(activity).filter(k => activity[k] > 0).sort();
  if (!keys.length) return { dailyAvg: 0, daysLearned: 0, longestStreak: 0, currentStreak: 0, total: 0 };

  const DAY = 86400000;
  const total = keys.reduce((s, k) => s + (Number(activity[k]) || 0), 0);
  const today = localDateKey();
  const yesterday = localDateKey(new Date(Date.now() - DAY));

  const firstDate = new Date(keys[0] + "T00:00:00");
  const todayDate = new Date(today + "T00:00:00");
  const totalDays = Math.max(1, Math.round((todayDate - firstDate) / DAY) + 1);
  const daysLearned = Math.round((keys.length / totalDays) * 100);
  const dailyAvg = Math.round(total / keys.length);

  // Longest streak — walk sorted keys, count consecutive-day runs.
  let longest = 1, run = 1;
  for (let i = 1; i < keys.length; i++) {
    const prev = new Date(keys[i - 1] + "T00:00:00");
    const cur  = new Date(keys[i]     + "T00:00:00");
    const diff = Math.round((cur - prev) / DAY);
    if (diff === 1) { run++; if (run > longest) longest = run; }
    else if (diff > 1) { run = 1; }
    // diff === 0 shouldn't happen with deduped keys; ignore.
  }

  // Current streak — anchor is today (if studied) or yesterday (grace day).
  // Anything older means the chain is already broken.
  let cs = 0;
  const lastKey = keys[keys.length - 1];
  if (lastKey === today || lastKey === yesterday) {
    const cursor = new Date(lastKey + "T00:00:00");
    while (activity[localDateKey(cursor)] > 0) {
      cs++;
      cursor.setDate(cursor.getDate() - 1);
    }
  }

  return { dailyAvg, daysLearned, longestStreak: longest, currentStreak: cs, total };
}

// ─── Учёт времени (per-language) ─────────────────────────────────────────────
// Хранилище: {lang: {dateKey: {listen, read}}}.

const TIME_KEY = "ac_time_by_lang";

const getTimeRaw = () => {
  try { return JSON.parse(localStorage.getItem(TIME_KEY) || "{}"); } catch { return {}; }
};

/** Без lang — суммирует по всем языкам. С lang — данные только этого языка. */
export const getTimeData = (lang) => {
  const raw = getTimeRaw();
  if (lang != null) return raw[normLang(lang)] || {};
  const out = {};
  Object.values(raw).forEach(byDate => {
    Object.entries(byDate || {}).forEach(([k, v]) => {
      if (!out[k]) out[k] = { listen: 0, read: 0 };
      out[k].listen += (Number(v?.listen) || 0);
      out[k].read   += (Number(v?.read)   || 0);
    });
  });
  return out;
};

export const getTimeLangs = () => Object.keys(getTimeRaw()).filter(l => l);

/** Добавляет секунды прослушивания/чтения на сегодня для языка. */
export const recordTime = (type, seconds, lang = "") => {
  if (!seconds || seconds < 1) return;
  try {
    const raw = getTimeRaw();
    const L = recLang(lang);
    if (!raw[L]) raw[L] = {};
    const today = localDateKey();
    if (!raw[L][today]) raw[L][today] = { listen: 0, read: 0 };
    raw[L][today][type] = (raw[L][today][type] || 0) + Math.round(seconds);
    localStorage.setItem(TIME_KEY, JSON.stringify(raw));
    try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  } catch {}
};

/**
 * Manual adjustment of listen/read seconds on today's bucket. delta can be
 * negative; the bucket is clamped to >= 0 so we never end up with phantom
 * negative totals. Used by the StatsScreen ± editors when the user wants to
 * credit listening done outside the app, or strip out a wrong entry (e.g.
 * fell asleep with the player on).
 */
export const adjustTime = (type, deltaSeconds, lang = "") => {
  const delta = Math.round(Number(deltaSeconds) || 0);
  if (!delta) return;
  try {
    const raw = getTimeRaw();
    const L = normLang(lang);
    if (!raw[L]) raw[L] = {};
    const today = localDateKey();
    if (!raw[L][today]) raw[L][today] = { listen: 0, read: 0 };
    const cur = Number(raw[L][today][type] || 0);
    raw[L][today][type] = Math.max(0, cur + delta);
    localStorage.setItem(TIME_KEY, JSON.stringify(raw));
    try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  } catch {}
};

/** Суммарное время прослушивания и чтения за всё время (в минутах). */
export const getTotalTime = (lang) => {
  const d = getTimeData(lang);
  let listen = 0, read = 0;
  Object.values(d).forEach(v => { listen += (v.listen || 0); read += (v.read || 0); });
  return { listenMin: Math.round(listen / 60), readMin: Math.round(read / 60) };
};

// ─── Учёт прочитанных слов (per-language) ────────────────────────────────────

export const AC_WORDS_KEY = 'ac_words_read_by_lang';

const getWordsRaw = () => {
  try { return JSON.parse(localStorage.getItem(AC_WORDS_KEY) || '{}'); } catch { return {}; }
};

/** Без lang — сумма по всем. С lang — только этот язык. Возвращает {dateKey: count}. */
export const getWordsReadData = (lang) => {
  const raw = getWordsRaw();
  if (lang != null) return raw[normLang(lang)] || {};
  const out = {};
  Object.values(raw).forEach(byDate => {
    Object.entries(byDate || {}).forEach(([k, v]) => { out[k] = (out[k] || 0) + (Number(v) || 0); });
  });
  return out;
};

export const getWordsLangs = () => Object.keys(getWordsRaw()).filter(l => l);

/** Записывает n прочитанных слов на сегодня для языка. */
export const recordWordsRead = (n, lang = "") => {
  if (!n || n < 1) return;
  try {
    const raw = getWordsRaw();
    const L = recLang(lang);
    if (!raw[L]) raw[L] = {};
    const today = localDateKey();
    raw[L][today] = (raw[L][today] || 0) + Math.round(n);
    localStorage.setItem(AC_WORDS_KEY, JSON.stringify(raw));
    try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  } catch {}
};

/** Manual ± of words-read on today's bucket. Clamps to >= 0. See adjustTime. */
export const adjustWordsRead = (delta, lang = "") => {
  const d = Math.round(Number(delta) || 0);
  if (!d) return;
  try {
    const raw = getWordsRaw();
    const L = normLang(lang);
    if (!raw[L]) raw[L] = {};
    const today = localDateKey();
    const cur = Number(raw[L][today] || 0);
    raw[L][today] = Math.max(0, cur + d);
    localStorage.setItem(AC_WORDS_KEY, JSON.stringify(raw));
    try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  } catch {}
};

/** Manual ± of activity (cards reviewed) on today's bucket. Clamps to >= 0. */
export const adjustActivity = (delta, lang = "") => {
  const d = Math.round(Number(delta) || 0);
  if (!d) return;
  try {
    const raw = JSON.parse(localStorage.getItem(ACT_KEY) || "{}");
    const L = normLang(lang);
    if (!raw[L]) raw[L] = {};
    const today = localDateKey();
    const cur = Number(raw[L][today] || 0);
    raw[L][today] = Math.max(0, cur + d);
    localStorage.setItem(ACT_KEY, JSON.stringify(raw));
    try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  } catch {}
};

/**
 * Wipe ALL stats (activity + listen/read time + words-read) for one language.
 * Returns a snapshot the caller can hand to restoreLangStats() for undo.
 * Content (projects, cards, text projects) is NOT touched — only the
 * accumulated learning metrics for this language code.
 */
export const clearLangStats = (lang) => {
  const L = normLang(lang);
  const snap = { activity: null, time: null, words: null };
  try {
    const a = JSON.parse(localStorage.getItem(ACT_KEY) || "{}");
    if (a[L]) { snap.activity = a[L]; delete a[L]; localStorage.setItem(ACT_KEY, JSON.stringify(a)); }
  } catch {}
  try {
    const t = JSON.parse(localStorage.getItem(TIME_KEY) || "{}");
    if (t[L]) { snap.time = t[L]; delete t[L]; localStorage.setItem(TIME_KEY, JSON.stringify(t)); }
  } catch {}
  try {
    const w = JSON.parse(localStorage.getItem(AC_WORDS_KEY) || "{}");
    if (w[L]) { snap.words = w[L]; delete w[L]; localStorage.setItem(AC_WORDS_KEY, JSON.stringify(w)); }
  } catch {}
  try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
  return snap;
};

/** Reverse of clearLangStats — restores a snapshot back into storage. */
export const restoreLangStats = (lang, snap) => {
  if (!snap) return;
  const L = normLang(lang);
  try {
    if (snap.activity) {
      const a = JSON.parse(localStorage.getItem(ACT_KEY) || "{}");
      a[L] = snap.activity;
      localStorage.setItem(ACT_KEY, JSON.stringify(a));
    }
  } catch {}
  try {
    if (snap.time) {
      const t = JSON.parse(localStorage.getItem(TIME_KEY) || "{}");
      t[L] = snap.time;
      localStorage.setItem(TIME_KEY, JSON.stringify(t));
    }
  } catch {}
  try {
    if (snap.words) {
      const w = JSON.parse(localStorage.getItem(AC_WORDS_KEY) || "{}");
      w[L] = snap.words;
      localStorage.setItem(AC_WORDS_KEY, JSON.stringify(w));
    }
  } catch {}
  try { window.dispatchEvent(new CustomEvent(ACT_EVT)); } catch {}
};

/** Суммарное кол-во слов за period (days=null → всё время), по lang или общее. */
export const getTotalWordsRead = (days, lang) => {
  const d = getWordsReadData(lang);
  const cutoff = days ? new Date(Date.now() - days * 864e5).toISOString().slice(0, 10) : null;
  return Object.entries(d).reduce((s, [k, v]) => (!cutoff || k >= cutoff) ? s + v : s, 0);
};

/** Суммарное время за period (days=null → всё время) по lang или общее. */
export const getTotalTimePeriod = (days, lang) => {
  const d = getTimeData(lang);
  const cutoff = days ? new Date(Date.now() - days * 864e5).toISOString().slice(0, 10) : null;
  let listen = 0, read = 0;
  Object.entries(d).forEach(([k, v]) => {
    if (!cutoff || k >= cutoff) { listen += (v.listen || 0); read += (v.read || 0); }
  });
  return { listenMin: Math.round(listen / 60), readMin: Math.round(read / 60) };
};

/** Объединённый список языков, по которым есть хоть какие-то данные. */
/**
 * Дни, когда человек занимался хоть чем-то: карточки, чтение (слова) или
 * слушание/чтение от минуты. По ним считается стрик — раньше считались только карточки.
 */
export const getStudyDays = (lang) => {
  const out = { ...getActivityData(lang) };
  Object.entries(getTimeData(lang)).forEach(([k, v]) => {
    if ((Number(v?.listen) || 0) + (Number(v?.read) || 0) >= 60) out[k] = (out[k] || 0) + 1;
  });
  Object.entries(getWordsReadData(lang)).forEach(([k, v]) => { if (Number(v) > 0) out[k] = (out[k] || 0) + 1; });
  return out;
};

export const getAllStatsLangs = () => {
  const s = new Set([...getActivityLangs(), ...getTimeLangs(), ...getWordsLangs()]);
  return Array.from(s);
};

/** Все «корзины» статистики, включая записи без языка (''). Для «очистить всё». */
export const getAllStatsBuckets = () => {
  const keys = (k) => { try { return Object.keys(JSON.parse(localStorage.getItem(k) || "{}")); } catch { return []; } };
  return Array.from(new Set([...keys(ACT_KEY), ...keys(TIME_KEY), ...keys(AC_WORDS_KEY)]));
};

/** Есть ли старые записи без языка — их показывает «Статистика», чтобы сумма сходилась. */
export const hasUnassignedStats = () => {
  const has = (k) => { try { const v = JSON.parse(localStorage.getItem(k) || "{}")[""]; return !!v && Object.keys(v).length > 0; } catch { return false; } };
  return has(ACT_KEY) || has(TIME_KEY) || has(AC_WORDS_KEY);
};

// ─── Миграция старых ключей ──────────────────────────────────────────────────
// Старый формат был без языка: ac_activity / ac_time / ac_words_read.
// По решению пользователя: при первом запуске после обновления — удаляем.
// Используется флаг ac_stats_migrated_v2 чтобы выполнить ровно один раз.
export const migrateStatsV2 = () => {
  try {
    if (localStorage.getItem("ac_stats_migrated_v2")) return;
    localStorage.removeItem("ac_activity");
    localStorage.removeItem("ac_time");
    localStorage.removeItem("ac_words_read");
    localStorage.setItem("ac_stats_migrated_v2", "1");
  } catch {}
};


// ─── Словарный квиз ───────────────────────────────────────────────────────────

/** Regex для извлечения слов из текста (Latin, Кирилл, CJK) */
const VQUIZ_RE = /[a-zA-ZÀ-ÿа-яА-ЯёЁ一-鿿぀-ヿ]+/g;

/** Возвращает уникальные строчные слова из текста */
export const extractVocabWords = (text) =>
  [...new Set((text.match(VQUIZ_RE) || []).map(w => w.toLowerCase()))];

// ─── Подсчёт слов в тексте (для карточки проекта) ─────────────────────────────
// Японский/китайский пишутся без пробелов — `text.match(/\S+/g).length` для них
// возвращает почти всегда 1 (весь абзац как одна последовательность не-пробелов).
// Эвристика для CJK: число CJK-символов делим на средний размер слова.
//
// Линвистические опорные значения:
//   • японский: среднее слово ~1.6 знака (kanji 1-2 + kana хвост) → делим на 1.7
//   • китайский: ~1.5 знака → делим на 1.5
//   • корейский (хангыль) состоит из слогов, слова разделены пробелами → пробельный счёт
// Для смешанных текстов (например ja + латинские названия) считаем CJK-часть
// по эвристике, латинскую — пробельным split, и суммируем.
const CJK_CHAR_RE  = /[぀-ゟ゠-ヿ一-鿿]/g; // hiragana, katakana, CJK Unified
const NON_CJK_RUN  = /[^぀-ゟ゠-ヿ一-鿿]+/g;
export const countWords = (text, lang = '') => {
  if (!text) return 0;
  const isCjk = lang === 'ja' || lang === 'zh' || CJK_CHAR_RE.test(text.slice(0, 2000));
  if (!isCjk) {
    // Латиница, кириллица — стандартный пробельный split.
    return (text.match(/\S+/g) || []).length;
  }
  // CJK-часть: чары / 1.7 для японского, / 1.5 для китайского.
  const cjkChars = (text.match(CJK_CHAR_RE) || []).length;
  const divisor  = lang === 'zh' ? 1.5 : 1.7;
  const cjkWords = Math.round(cjkChars / divisor);
  // Не-CJK части (латинские/кириллические вкрапления): пробельным split.
  let otherWords = 0;
  const otherChunks = text.match(NON_CJK_RUN) || [];
  for (const chunk of otherChunks) {
    otherWords += (chunk.match(/[A-Za-zÀ-ÿА-Яа-яЁё]+/g) || []).length;
  }
  return cjkWords + otherWords;
};
